import { auCentime } from './vat';
import { today, uid } from './utils';

/**
 * Lignes d'un inventaire par comptage.
 *
 * <p>Chaque article du stock se lit avec sa quantité théorique — celle que la
 * fiche annonce — et, s'il a été compté, la quantité trouvée en rayon. L'écart
 * est ce que le comptage corrige ; sa valeur au prix d'achat dit ce que la
 * démarque a coûté, pour qui a le droit de le lire.
 *
 * @param {Array} stock articles du stock
 * @param {Record<string, string|number>} comptes quantités comptées, par identifiant d'article
 * @returns {Array<{art: object, theorique: number, compte: number|null, ecart: number,
 *           valeur: number}>} une ligne par article, comptée ou non
 */
export function lignesInventaire(stock, comptes) {
  return (stock || []).map((art) => {
    const theorique = +art.stock || 0;
    const brut = (comptes || {})[art.id];
    const compte = brut === '' || brut == null || Number.isNaN(+brut) ? null : Math.max(0, +brut);
    const ecart = compte == null ? 0 : compte - theorique;
    return {
      art,
      theorique,
      compte,
      ecart,
      valeur: auCentime(ecart * (+art.pa || 0)),
    };
  });
}

/**
 * Bilan d'un comptage : ce qui a été compté, ce qui s'écarte, et pour combien.
 *
 * @param {Array} lignes lignes d'inventaire, telles que {@link lignesInventaire} les rend
 * @returns {{comptes: number, ecartes: number, manques: number, surplus: number,
 *            valeur: number}} le bilan
 */
export function bilanInventaire(lignes) {
  const comptees = (lignes || []).filter((l) => l.compte != null);
  const ecartees = comptees.filter((l) => l.ecart !== 0);
  return {
    comptes: comptees.length,
    ecartes: ecartees.length,
    manques: ecartees.filter((l) => l.ecart < 0).length,
    surplus: ecartees.filter((l) => l.ecart > 0).length,
    valeur: auCentime(ecartees.reduce((s, l) => s + l.valeur, 0)),
  };
}

/**
 * Applique un comptage au stock : une quantité corrigée et un mouvement
 * d'ajustement par article écarté.
 *
 * <p>Le mouvement s'écrit dans le journal des sorties de l'article, sous le
 * motif « inventaire » : une quantité positive pour un manque, négative pour un
 * surplus. C'est ce qui explique, plus tard, pourquoi la fiche ne dit plus ce
 * qu'elle disait — un écart sans justification ne se défend pas devant un
 * contrôle.
 *
 * @param {Array} stock articles du stock
 * @param {Array} lignes lignes d'inventaire
 * @param {{commentaire?: string, utilisateur?: string, date?: string, heure?: string}} [contexte]
 *        ce qui accompagne chaque mouvement
 * @returns {Array} le stock corrigé, articles non comptés inchangés
 */
export function appliqueInventaire(stock, lignes, contexte = {}) {
  const parId = new Map((lignes || []).map((l) => [l.art.id, l]));
  const date = contexte.date || today();
  const heure = contexte.heure || heureCourante();
  return (stock || []).map((art) => {
    const l = parId.get(art.id);
    if (!l || l.compte == null || l.ecart === 0) return art;
    return {
      ...art,
      stock: l.compte,
      sorties: [
        {
          id: uid(),
          qte: -l.ecart,
          motif: 'inventaire',
          note:
            'Inventaire : ' +
            l.theorique +
            ' → ' +
            l.compte +
            (contexte.commentaire ? ' · ' + contexte.commentaire : ''),
          date,
          heure,
          empNom: contexte.utilisateur || '',
        },
        ...(art.sorties || []),
      ],
    };
  });
}

/** Heure courante, au format HH:mm. */
export function heureCourante() {
  const h = new Date();
  return String(h.getHours()).padStart(2, '0') + ':' + String(h.getMinutes()).padStart(2, '0');
}

/** Clé du brouillon de comptage d'une société dans le navigateur. */
export const cleBrouillon = (socId) => 'st_inventaire_' + (socId == null ? 'soc' : socId);

/**
 * Brouillon de comptage en cours, s'il en reste un sur cet appareil.
 *
 * <p>Un inventaire se fait rayon par rayon, souvent sur plusieurs heures : le
 * comptage entamé doit survivre à un onglet fermé. Il reste sur l'appareil qui
 * compte, sans passer par le serveur — rien n'est corrigé tant que le comptage
 * n'est pas validé.
 *
 * @param {any} socId identifiant de la société
 * @returns {{date: string, comptes: Record<string, string>, commentaire: string}|null} le brouillon
 */
export function litBrouillon(socId) {
  try {
    const brut = localStorage.getItem(cleBrouillon(socId));
    if (!brut) return null;
    const b = JSON.parse(brut);
    if (!b || typeof b !== 'object' || !b.comptes || !Object.keys(b.comptes).length) return null;
    return { date: b.date || '', comptes: b.comptes, commentaire: b.commentaire || '' };
  } catch (e) {
    return null;
  }
}

/**
 * Retient le comptage en cours, ou l'efface s'il est vide.
 *
 * @param {any} socId identifiant de la société
 * @param {{comptes: Record<string, string>, commentaire?: string}} brouillon le comptage
 */
export function ecritBrouillon(socId, brouillon) {
  try {
    const comptes = Object.fromEntries(
      Object.entries(brouillon?.comptes || {}).filter(([, v]) => v !== '' && v != null),
    );
    if (!Object.keys(comptes).length && !brouillon?.commentaire) {
      localStorage.removeItem(cleBrouillon(socId));
      return;
    }
    localStorage.setItem(
      cleBrouillon(socId),
      JSON.stringify({ date: today(), comptes, commentaire: brouillon?.commentaire || '' }),
    );
  } catch (e) {}
}

/**
 * Efface le brouillon : le comptage est validé, ou abandonné.
 *
 * @param {any} socId identifiant de la société
 */
export function effaceBrouillon(socId) {
  try {
    localStorage.removeItem(cleBrouillon(socId));
  } catch (e) {}
}
